import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Plus,
    Settings,
    Package,
    Truck,
    Wrench,
    Monitor,
    Smartphone,
    TrendingUp,
    AlertCircle,
    DollarSign,
    ArrowLeft,
    ArrowUp,
    ArrowDown,
    Star,
    Copy,
    Edit,
    Edit3,
    Hammer,
    Anchor,
    Armchair,
    ClipboardCheck,
    Loader2,
    Trash2,
    Archive,
    GripVertical,
    FileText,
    AlertTriangle,
    X,
    Save,
    Building2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { cn } from '../../components/ui/utils';
import { Badge } from '../../components/ui/badge';
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../../components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import api from '../../utils/api';
import { Asset, AssetCustomField } from '../../types/asset';
import { toast } from 'sonner';
import AccessRestricted from '../../components/AccessRestricted';
import ElectricalSettings from './components/ElectricalSettings';
import LiftingSettings from './components/LiftingSettings';

// Types for Inspection Templates
interface InspectionItem {
    id: number;
    question: string;
    listOrder: number;
    isMandatory: boolean;
    isArchived: boolean;
    createdAt: string;
    updatedAt: string;
}

interface InspectionTemplate {
    id: number;
    name: string;
    assetType: string;
    version: number;
    isDefault: boolean;
    isActive: boolean;
    items: InspectionItem[];
    activeItemCount: number;
    createdAt: string;
    updatedAt: string;
}

const assetTypeCategories = [
    { id: 'Vehicle', label: 'Vehicles', icon: <Truck size={16} /> },
    { id: 'Plant & Equipment', label: 'Plant & Equipment', icon: <Wrench size={16} /> },
    { id: 'Tools', label: 'Tools', icon: <Hammer size={16} /> },
    { id: 'Lifting/Slings', label: 'Lifting/Slings', icon: <Anchor size={16} /> },
    { id: 'IT Equipment', label: 'IT Equipment', icon: <Monitor size={16} /> },
    { id: 'Mobile Devices', label: 'Mobile Devices', icon: <Smartphone size={16} /> },
    { id: 'Furniture', label: 'Furniture', icon: <Armchair size={16} /> },
];

const AssetSettings: React.FC = () => {
    const ability = useAbility(AbilityContext);
    const navigate = useNavigate();
    const [selectedAssetType, setSelectedAssetType] = useState('Vehicle');
    const [templates, setTemplates] = useState<InspectionTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        total: 0,
        value: 0,
        active: 0,
        available: 0,
        needAttention: 0
    });

    // Selected template for viewing items
    const [selectedTemplate, setSelectedTemplate] = useState<InspectionTemplate | null>(null);
    const [templateItems, setTemplateItems] = useState<InspectionItem[]>([]);
    const [itemsLoading, setItemsLoading] = useState(false);

    // Rename dialog state
    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [renamingTemplate, setRenamingTemplate] = useState<InspectionTemplate | null>(null);
    const [newTemplateName, setNewTemplateName] = useState('');
    const [saving, setSaving] = useState(false);

    // New template dialog state
    const [newTemplateDialogOpen, setNewTemplateDialogOpen] = useState(false);
    const [createTemplateName, setCreateTemplateName] = useState('');

    // Add item dialog state
    const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
    const [newItemQuestion, setNewItemQuestion] = useState('');
    const [newItemMandatory, setNewItemMandatory] = useState(false);

    // Edit item dialog state
    const [editItemDialogOpen, setEditItemDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<InspectionItem | null>(null);
    const [editItemQuestion, setEditItemQuestion] = useState('');
    const [editItemMandatory, setEditItemMandatory] = useState(false);

    useEffect(() => {
        fetchStats();
    }, []);

    useEffect(() => {
        fetchTemplates();
        setSelectedTemplate(null);
        setTemplateItems([]);
    }, [selectedAssetType]);

    const fetchStats = async () => {
        try {
            const response = await api.get<Asset[]>('/api/assets');
            const total = response.length;
            const value = response.reduce((acc, curr) => acc + (Number(curr.purchasePrice) || 0), 0);
            const active = response.filter(a => a.status === 'Assigned').length;
            const available = response.filter(a => a.status === 'Available').length;
            const needAttention = response.filter(a => a.status === 'Needs Repair' || a.status === 'Damaged').length;
            setStats({ total, value, active, available, needAttention });
        } catch (error) {
            console.error('Error fetching asset stats:', error);
        }
    };

    // Custom fields state
    const [activeTab, setActiveTab] = useState<'inspection' | 'fields' | 'organization' | 'electrical' | 'lifting'>('inspection');
    const [customFields, setCustomFields] = useState<AssetCustomField[]>([]);
    const [organizations, setOrganizations] = useState<any[]>([]);
    // Non-null opens the delete-organization confirmation dialog
    const [organizationToDelete, setOrganizationToDelete] = useState<{ id: number; companyName: string } | null>(null);
    const [newOrgName, setNewOrgName] = useState('');
    const [orgLoading, setOrgLoading] = useState(false);
    const [fieldsLoading, setFieldsLoading] = useState(false);

    // Add custom field dialog
    const [addFieldDialogOpen, setAddFieldDialogOpen] = useState(false);
    const [newFieldName, setNewFieldName] = useState('');
    const [newFieldType, setNewFieldType] = useState<'text' | 'number' | 'select' | 'boolean' | 'date'>('text');
    const [newFieldOptions, setNewFieldOptions] = useState('');
    const [newFieldRequired, setNewFieldRequired] = useState(false);
    const [createConfirmDialogOpen, setCreateConfirmDialogOpen] = useState(false);

    // Delete field dialog
    const [deleteFieldDialogOpen, setDeleteFieldDialogOpen] = useState(false);
    const [fieldToDelete, setFieldToDelete] = useState<AssetCustomField | null>(null);
    const [deleteConfirmationText, setDeleteConfirmationText] = useState('');

    useEffect(() => {
        if (activeTab === 'fields') {
            fetchCustomFields();
        } else if (activeTab === 'organization') {
            fetchOrganization();
        }
    }, [selectedAssetType, activeTab]);

    const fetchOrganization = async () => {
        try {
            setOrgLoading(true);
            const response = await api.get('/api/organization');
            if (response && (response as any).organizations) {
                setOrganizations((response as any).organizations);
            }
        } catch (error) {
            console.error('Error fetching organizations:', error);
            toast.error('Failed to load organizations');
        } finally {
            setOrgLoading(false);
        }
    };

    const handleAddOrganization = async () => {
        if (!newOrgName.trim()) {
            toast.error('Organization name is required');
            return;
        }
        try {
            setSaving(true);
            const response = await api.post('/api/organization', { companyName: newOrgName.trim() });
            if (response && (response as any).success) {
                setOrganizations([...organizations, (response as any).organization]);
                setNewOrgName('');
                toast.success('Organization added successfully');
            }
        } catch (error) {
            console.error('Error adding organization:', error);
            toast.error('Failed to add organization');
        } finally {
            setSaving(false);
        }
    };

    const confirmDeleteOrganization = async () => {
        if (organizationToDelete === null) return;
        try {
            setSaving(true);
            await api.delete(`/api/organization/${organizationToDelete.id}`);
            setOrganizations(organizations.filter(o => o.id !== organizationToDelete.id));
            toast.success('Organization deleted successfully');
        } catch (error: any) {
            console.error('Error deleting organization:', error);
            toast.error(error.response?.data?.message || 'Failed to delete organization');
        } finally {
            setSaving(false);
            setOrganizationToDelete(null);
        }
    };

    const fetchCustomFields = async () => {
        try {
            setFieldsLoading(true);
            const response = await api.get<AssetCustomField[]>(`/api/asset-custom-fields?assetType=${encodeURIComponent(selectedAssetType)}`);
            setCustomFields(response || []);
        } catch (error) {
            console.error('Error fetching custom fields:', error);
            toast.error('Failed to load custom fields');
        } finally {
            setFieldsLoading(false);
        }
    };

    const handleCreateField = async () => {
        if (!newFieldName.trim()) {
            toast.error('Field name is required');
            return;
        }
        try {
            setSaving(true);
            const options = newFieldType === 'select' ? newFieldOptions.split(',').map(o => o.trim()).filter(Boolean) : undefined;

            await api.post('/api/asset-custom-fields', {
                assetType: selectedAssetType,
                fieldName: newFieldName.trim(),
                fieldType: newFieldType,
                options,
                isRequired: newFieldRequired
            });
            toast.success('Field created successfully');
            setAddFieldDialogOpen(false);
            setNewFieldName('');
            setNewFieldType('text');
            setNewFieldOptions('');
            setNewFieldRequired(false);
            fetchCustomFields();
        } catch (error) {
            console.error('Error creating field:', error);
            toast.error('Failed to create custom field');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleFieldActive = async (field: AssetCustomField) => {
        try {
            await api.patch(`/api/asset-custom-fields/${field.id}`, { isActive: !field.isActive });
            toast.success(`Field ${!field.isActive ? 'enabled' : 'disabled'}`);
            setCustomFields(prev => prev.map(f => f.id === field.id ? { ...f, isActive: !f.isActive } : f));
        } catch (err) {
            toast.error('Failed to update field status');
        }
    };

    const promptDeleteField = (field: AssetCustomField) => {
        setFieldToDelete(field);
        setDeleteConfirmationText('');
        setDeleteFieldDialogOpen(true);
    };

    const handleDeleteField = async () => {
        if (!fieldToDelete) return;
        try {
            await api.delete(`/api/asset-custom-fields/${fieldToDelete.id}`);
            toast.success('Field deleted');
            setDeleteFieldDialogOpen(false);
            setFieldToDelete(null);
            fetchCustomFields();
        } catch (err) {
            toast.error('Failed to delete field');
        }
    };

    const handleMoveUp = async (index: number) => {
        if (index === 0) return;
        const newFields = [...customFields];
        const current = newFields[index];
        const prev = newFields[index - 1];

        // Swap listOrder
        const tempOrder = current.listOrder;
        current.listOrder = prev.listOrder;
        prev.listOrder = tempOrder;

        // Swap array positions
        newFields[index] = prev;
        newFields[index - 1] = current;

        setCustomFields(newFields);

        try {
            await api.patch('/api/asset-custom-fields/reorder', {
                fields: [
                    { id: current.id, listOrder: current.listOrder },
                    { id: prev.id, listOrder: prev.listOrder }
                ]
            });
        } catch (err) {
            console.error('Error reordering fields:', err);
            toast.error('Failed to reorder fields');
            fetchCustomFields(); // revert on fail
        }
    };

    const handleMoveDown = async (index: number) => {
        if (index === customFields.length - 1) return;
        const newFields = [...customFields];
        const current = newFields[index];
        const next = newFields[index + 1];

        // Swap listOrder
        const tempOrder = current.listOrder;
        current.listOrder = next.listOrder;
        next.listOrder = tempOrder;

        // Swap array positions
        newFields[index] = next;
        newFields[index + 1] = current;

        setCustomFields(newFields);

        try {
            await api.patch('/api/asset-custom-fields/reorder', {
                fields: [
                    { id: current.id, listOrder: current.listOrder },
                    { id: next.id, listOrder: next.listOrder }
                ]
            });
        } catch (err) {
            console.error('Error reordering fields:', err);
            toast.error('Failed to reorder fields');
            fetchCustomFields(); // revert on fail
        }
    };

    if (!ability.can(ACTIONS.READ, RESOURCES.ASSET_SETTINGS)) {
        return <AccessRestricted />;
    }

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            const response = await api.get<any>(`/api/inspection-templates?assetType=${encodeURIComponent(selectedAssetType)}`);
            if (response && Array.isArray(response)) {
                setTemplates(response);
            } else {
                setTemplates([]);
            }
        } catch (error) {
            console.error('Error fetching inspection templates:', error);
            setTemplates([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchTemplateItems = async (template: InspectionTemplate) => {
        try {
            setItemsLoading(true);
            const response = await api.get<InspectionItem[]>(`/api/inspection-templates/${template.id}/items`);
            if (response && Array.isArray(response)) {
                setTemplateItems(response);
            } else {
                setTemplateItems([]);
            }
        } catch (error) {
            console.error('Error fetching template items:', error);
            setTemplateItems([]);
        } finally {
            setItemsLoading(false);
        }
    };

    const handleSelectTemplate = (template: InspectionTemplate) => {
        setSelectedTemplate(template);
        fetchTemplateItems(template);
    };

    // ---- Template actions ----

    const handleRename = (template: InspectionTemplate) => {
        setRenamingTemplate(template);
        setNewTemplateName(template.name);
        setRenameDialogOpen(true);
    };

    const handleRenameSubmit = async () => {
        if (!renamingTemplate || !newTemplateName.trim()) return;
        try {
            setSaving(true);
            await api.patch(`/api/inspection-templates/${renamingTemplate.id}`, {
                name: newTemplateName.trim(),
            });
            toast.success('Template renamed successfully');
            setRenameDialogOpen(false);
            fetchTemplates();
            if (selectedTemplate?.id === renamingTemplate.id) {
                setSelectedTemplate(prev => prev ? { ...prev, name: newTemplateName.trim() } : null);
            }
        } catch (error) {
            console.error('Error renaming template:', error);
            toast.error('Failed to rename template');
        } finally {
            setSaving(false);
        }
    };

    const handleClone = async (template: InspectionTemplate) => {
        try {
            await api.post(`/api/inspection-templates/${template.id}/clone`, {});
            toast.success('Template cloned successfully');
            fetchTemplates();
        } catch (error) {
            console.error('Error cloning template:', error);
            toast.error('Failed to clone template');
        }
    };

    const handleSetDefault = async (template: InspectionTemplate) => {
        if (template.isDefault) return;
        try {
            await api.patch(`/api/inspection-templates/${template.id}`, {
                isDefault: true,
            });
            toast.success(`${template.name} set as default`);
            fetchTemplates();
        } catch (error) {
            console.error('Error setting default template:', error);
            toast.error('Failed to set default template');
        }
    };

    const handleArchiveTemplate = async (template: InspectionTemplate) => {
        if (template.isDefault) {
            toast.error('Cannot archive a default template');
            return;
        }
        try {
            await api.patch(`/api/inspection-templates/${template.id}`, {
                isActive: false,
            });
            toast.success(`${template.name} has been archived`);
            fetchTemplates();
            if (selectedTemplate?.id === template.id) {
                setSelectedTemplate(null);
            }
        } catch (error) {
            console.error('Error archiving template:', error);
            toast.error('Failed to archive template');
        }
    };

    const handleCreateTemplate = async () => {
        if (!createTemplateName.trim()) {
            toast.error('Template name is required');
            return;
        }
        try {
            setSaving(true);
            await api.post('/api/inspection-templates', {
                name: createTemplateName.trim(),
                assetType: selectedAssetType,
            });
            toast.success('Template created successfully');
            setNewTemplateDialogOpen(false);
            setCreateTemplateName('');
            fetchTemplates();
        } catch (error) {
            console.error('Error creating template:', error);
            toast.error('Failed to create template');
        } finally {
            setSaving(false);
        }
    };

    // ---- Item actions ----

    const handleAddItem = async () => {
        if (!selectedTemplate || !newItemQuestion.trim()) return;
        try {
            setSaving(true);
            await api.post(`/api/inspection-templates/${selectedTemplate.id}/items`, {
                question: newItemQuestion.trim(),
                isMandatory: newItemMandatory,
            });
            toast.success('Item added successfully');
            setAddItemDialogOpen(false);
            setNewItemQuestion('');
            setNewItemMandatory(false);
            fetchTemplateItems(selectedTemplate);
            fetchTemplates(); // Refresh counts
        } catch (error) {
            console.error('Error adding item:', error);
            toast.error('Failed to add item');
        } finally {
            setSaving(false);
        }
    };

    const handleEditItem = (item: InspectionItem) => {
        setEditingItem(item);
        setEditItemQuestion(item.question);
        setEditItemMandatory(item.isMandatory);
        setEditItemDialogOpen(true);
    };

    const handleEditItemSubmit = async () => {
        if (!selectedTemplate || !editingItem || !editItemQuestion.trim()) return;
        try {
            setSaving(true);
            await api.patch(`/api/inspection-templates/${selectedTemplate.id}/items/${editingItem.id}`, {
                question: editItemQuestion.trim(),
                isMandatory: editItemMandatory,
            });
            toast.success('Item updated successfully');
            setEditItemDialogOpen(false);
            fetchTemplateItems(selectedTemplate);
        } catch (error) {
            console.error('Error updating item:', error);
            toast.error('Failed to update item');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleMandatory = async (item: InspectionItem) => {
        if (!selectedTemplate) return;
        try {
            await api.patch(`/api/inspection-templates/${selectedTemplate.id}/items/${item.id}`, {
                isMandatory: !item.isMandatory,
            });
            setTemplateItems(prev =>
                prev.map(i => i.id === item.id ? { ...i, isMandatory: !i.isMandatory } : i)
            );
        } catch (error) {
            console.error('Error toggling mandatory:', error);
            toast.error('Failed to update item');
        }
    };

    const handleArchiveItem = async (item: InspectionItem) => {
        if (!selectedTemplate) return;
        try {
            await api.patch(`/api/inspection-templates/${selectedTemplate.id}/items/${item.id}`, {
                isArchived: !item.isArchived,
            });
            toast.success(item.isArchived ? 'Item restored' : 'Item archived');
            fetchTemplateItems(selectedTemplate);
            fetchTemplates(); // Refresh counts
        } catch (error) {
            console.error('Error archiving item:', error);
            toast.error('Failed to archive item');
        }
    };

    const activeTemplates = templates.filter(t => t.isActive);
    const selectedCategory = assetTypeCategories.find(c => c.id === selectedAssetType);

    // Split items into active and archived
    const activeItems = templateItems.filter(i => !i.isArchived);
    const archivedItems = templateItems.filter(i => i.isArchived);

    return (
        <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10 px-0 sm:px-4 lg:px-0">
            {/* Main Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="rounded-xl hover:bg-gray-100"
                        onClick={() => navigate('/assets')}
                    >
                        <ArrowLeft size={20} />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Asset Management</h1>
                        <p className="text-gray-500 text-sm font-medium">
                            Manage and track all company assets across departments
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group overflow-hidden relative">
                    <div className="flex justify-between items-start">
                        <div className="z-10">
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Total Assets</p>
                            <h3 className="text-2xl font-black text-gray-900 group-hover:scale-110 transition-transform origin-left">{stats.total}</h3>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-2xl group-hover:bg-blue-50 transition-colors z-10">
                            <Package className="text-gray-400 group-hover:text-blue-500 transition-colors" size={24} />
                        </div>
                    </div>
                </div>

                {ability.can(ACTIONS.READ, RESOURCES.ASSET_VALUE) && (
                    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group overflow-hidden relative">
                        <div className="flex justify-between items-start">
                            <div className="z-10">
                                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Total Value</p>
                                <h3 className="text-2xl font-black text-gray-900 font-mono group-hover:scale-110 transition-transform origin-left">
                                    ${stats.value.toLocaleString()}
                                </h3>
                            </div>
                            <div className="p-3 bg-green-50 rounded-2xl group-hover:bg-green-100 transition-colors z-10">
                                <DollarSign className="text-gray-400 group-hover:text-green-500 transition-colors" size={24} />
                            </div>
                        </div>
                    </div>
                )}

                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group overflow-hidden relative">
                    <div className="flex justify-between items-start">
                        <div className="z-10">
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Assigned Assets</p>
                            <h3 className="text-2xl font-black text-gray-900 group-hover:scale-110 transition-transform origin-left">{stats.active}</h3>
                            <div className="flex items-center gap-1.5 mt-2 text-green-600 text-[10px] font-bold uppercase tracking-wider">
                                <TrendingUp size={12} />
                                <span>Currently in use</span>
                            </div>
                        </div>
                        <div className="p-3 bg-green-50 rounded-2xl group-hover:bg-green-100 transition-colors z-10">
                            <TrendingUp className="text-green-600" size={24} />
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group overflow-hidden relative">
                    <div className="flex justify-between items-start">
                        <div className="z-10">
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Need Attention</p>
                            <h3 className="text-2xl font-black text-gray-900 group-hover:scale-110 transition-transform origin-left">{stats.needAttention}</h3>
                            <div className="flex items-center gap-1.5 mt-2 text-orange-600 text-[10px] font-bold uppercase tracking-wider">
                                <AlertCircle size={12} />
                                <span>Require action</span>
                            </div>
                        </div>
                        <div className="p-3 bg-orange-50 rounded-2xl group-hover:bg-orange-100 transition-colors z-10">
                            <AlertCircle className="text-orange-600" size={24} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation Tabs - matching the AssetDashboard style */}
            <div className="bg-gray-100 p-1 rounded-full w-fit flex h-auto">
                <button
                    className="rounded-full px-6 py-2 flex items-center gap-2 font-bold transition-all text-sm text-gray-500 hover:text-gray-700"
                    onClick={() => navigate('/assets')}
                >
                    <Package size={18} />
                    Assets
                </button>
                <button
                    className="rounded-full px-6 py-2 flex items-center gap-2 font-bold transition-all text-sm text-gray-500 hover:text-gray-700"
                    onClick={() => navigate('/assets')}
                >
                    <FileText size={18} />
                    Reports
                </button>
            </div>

            {/* Main Section Navigation */}
            <div className="flex items-center gap-6 border-b border-gray-200 mt-6 px-4 sm:px-0">
                <button
                    className={cn(
                        "pb-4 text-sm font-bold transition-all border-b-2 -mb-[1px]",
                        activeTab === 'inspection'
                            ? "border-slate-900 text-slate-900"
                            : "border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300"
                    )}
                    onClick={() => setActiveTab('inspection')}
                >
                    Inspection Templates
                </button>
                <button
                    className={cn(
                        "pb-4 text-sm font-bold transition-all border-b-2 -mb-[1px]",
                        activeTab === 'electrical'
                            ? "border-slate-900 text-slate-900"
                            : "border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300"
                    )}
                    onClick={() => setActiveTab('electrical')}
                >
                    Electrical Register
                </button>
                <button
                    className={cn(
                        "pb-4 text-sm font-bold transition-all border-b-2 -mb-[1px]",
                        activeTab === 'lifting'
                            ? "border-slate-900 text-slate-900"
                            : "border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300"
                    )}
                    onClick={() => setActiveTab('lifting')}
                >
                    Lifting Register
                </button>
                <button
                    className={cn(
                        "pb-4 text-sm font-bold transition-all border-b-2 -mb-[1px]",
                        activeTab === 'fields'
                            ? "border-slate-900 text-slate-900"
                            : "border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300"
                    )}
                    onClick={() => setActiveTab('fields')}
                >
                    Custom Fields
                </button>
                <button
                    className={cn(
                        "pb-4 text-sm font-bold transition-all border-b-2 -mb-[1px]",
                        activeTab === 'organization'
                            ? "border-slate-900 text-slate-900"
                            : "border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300"
                    )}
                    onClick={() => setActiveTab('organization')}
                >
                    Organization
                </button>
            </div>

            {/* Content Section */}
            <div className="space-y-6">
                <div>
                    <h2 className="text-xl font-bold text-gray-900 tracking-tight">
                        {activeTab === 'organization' ? 'Organization Settings' :
                            activeTab === 'inspection' ? 'Inspection Settings' :
                                activeTab === 'electrical' ? 'Electrical Register Settings' :
                                    activeTab === 'lifting' ? 'Lifting Register Settings' :
                                        'Custom Field Settings'}
                    </h2>
                    <p className="text-gray-500 text-sm font-medium mt-1">
                        {activeTab === 'inspection'
                            ? 'Manage multiple inspection templates for different asset types'
                            : activeTab === 'electrical'
                                ? 'Configure global defaults for Test & Tag inspections'
                                : activeTab === 'lifting'
                                    ? 'Configure global defaults for Lifting Equipment inspections'
                                    : activeTab === 'organization'
                                        ? 'Manage global organization details like Company Name used in print labels.'
                                        : 'Configure additional information fields for different asset types'}
                    </p>
                </div>

                {/* Asset Type Tabs */}
                {activeTab !== 'organization' && activeTab !== 'electrical' && activeTab !== 'lifting' && (
                    <div className="w-full">
                        <div className="flex items-center gap-1 overflow-x-auto no-scrollbar border-b border-gray-200">
                            {assetTypeCategories.map((type) => (
                                <button
                                    key={type.id}
                                    onClick={() => setSelectedAssetType(type.id)}
                                    className={cn(
                                        "flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-all whitespace-nowrap border-b-2 -mb-[1px]",
                                        selectedAssetType === type.id
                                            ? "border-blue-600 text-blue-600"
                                            : "border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300"
                                    )}
                                >
                                    {type.icon}
                                    {type.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === 'inspection' && (
                    <div className="space-y-8 animate-in fade-in duration-300">
                        <div className="space-y-4">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div>
                                    <h3 className="text-base font-bold text-gray-900">
                                        Templates for {selectedCategory?.label || selectedAssetType}
                                    </h3>
                                    <p className="text-gray-400 text-sm font-medium">
                                        {loading ? 'Loading...' : `${activeTemplates.length} template${activeTemplates.length !== 1 ? 's' : ''} available`}
                                    </p>
                                </div>
                                <Button
                                    className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 px-4 whitespace-nowrap w-fit"
                                    onClick={() => {
                                        setCreateTemplateName('');
                                        setNewTemplateDialogOpen(true);
                                    }}
                                >
                                    <Plus size={16} />
                                    <span>New Template</span>
                                </Button>
                            </div>

                            {/* Active Templates */}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm font-bold text-gray-900">Active Templates</p>
                                    <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-lg">
                                        {activeTemplates.length}
                                    </span>
                                </div>

                                {loading ? (
                                    <div className="space-y-3">
                                        {[...Array(2)].map((_, i) => (
                                            <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6 animate-pulse">
                                                <div className="h-5 bg-gray-100 rounded-lg w-48 mb-3" />
                                                <div className="h-4 bg-gray-50 rounded-lg w-32" />
                                            </div>
                                        ))}
                                    </div>
                                ) : activeTemplates.length === 0 ? (
                                    <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
                                        <div className="flex flex-col items-center">
                                            <div className="p-4 bg-gray-50 rounded-2xl mb-4">
                                                <ClipboardCheck size={32} className="text-gray-300" />
                                            </div>
                                            <p className="font-bold text-gray-500 text-sm">No templates yet</p>
                                            <p className="text-gray-400 text-xs mt-1 max-w-sm">
                                                Create an inspection template to start standardizing inspections for {selectedCategory?.label || selectedAssetType}
                                            </p>
                                            <Button
                                                className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 px-4 mt-4"
                                                onClick={() => {
                                                    setCreateTemplateName('');
                                                    setNewTemplateDialogOpen(true);
                                                }}
                                            >
                                                <Plus size={16} />
                                                <span>New Template</span>
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                        {activeTemplates.map((template) => (
                                            <div
                                                key={template.id}
                                                className={cn(
                                                    "bg-white rounded-2xl border shadow-sm hover:shadow-md transition-all group overflow-hidden cursor-pointer",
                                                    selectedTemplate?.id === template.id
                                                        ? "border-blue-300 ring-2 ring-blue-100"
                                                        : "border-gray-100"
                                                )}
                                                onClick={() => handleSelectTemplate(template)}
                                            >
                                                <div className="p-5 space-y-3">
                                                    {/* Template Header */}
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="flex items-center gap-3 min-w-0">
                                                            <div>
                                                                <h4 className="font-bold text-gray-900 text-sm truncate">
                                                                    {template.name}
                                                                </h4>
                                                                <p className="text-xs text-gray-400 font-medium mt-0.5">
                                                                    Version {template.version} • {template.activeItemCount ?? template.items?.length ?? 0} items
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleSetDefault(template);
                                                            }}
                                                            className={cn(
                                                                "shrink-0 transition-all",
                                                                template.isDefault
                                                                    ? "text-amber-400"
                                                                    : "text-gray-200 hover:text-amber-300"
                                                            )}
                                                            title={template.isDefault ? 'Default template' : 'Set as default'}
                                                        >
                                                            <Star
                                                                size={20}
                                                                fill={template.isDefault ? 'currentColor' : 'none'}
                                                            />
                                                        </button>
                                                    </div>

                                                    {/* Active Items Count */}
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs font-medium text-gray-500">Active Items</span>
                                                        <Badge variant="outline" className="rounded-lg bg-blue-50 text-blue-600 border-blue-100 text-[11px] font-bold px-2 py-0.5">
                                                            {template.activeItemCount ?? template.items?.length ?? 0}
                                                        </Badge>
                                                    </div>
                                                </div>

                                                {/* Template Actions */}
                                                <div className="px-5 pb-5 pt-2 mt-auto grid grid-cols-2 gap-2">
                                                    <Button
                                                        variant="outline"
                                                        className="w-full bg-white border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2 h-9 text-sm rounded-lg"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRename(template);
                                                        }}
                                                    >
                                                        <Edit3 size={14} />
                                                        Rename
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        className="w-full bg-white border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2 h-9 text-sm rounded-lg"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleClone(template);
                                                        }}
                                                    >
                                                        <Copy size={14} />
                                                        Clone
                                                    </Button>

                                                    {!template.isDefault && (
                                                        <>
                                                            <Button
                                                                variant="outline"
                                                                className="w-full bg-white border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2 h-9 text-sm rounded-lg"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleSetDefault(template);
                                                                }}
                                                            >
                                                                <Star size={14} />
                                                                Set Default
                                                            </Button>
                                                            <Button
                                                                variant="outline"
                                                                className="w-full bg-white border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2 h-9 text-sm rounded-lg"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleArchiveTemplate(template);
                                                                }}
                                                            >
                                                                <Archive size={14} />
                                                                Archive
                                                            </Button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* ============================================================ */}
                        {/* Checklist Items Section (shown when a template is selected) */}
                        {/* ============================================================ */}
                        {selectedTemplate && (
                            <div className="space-y-4 mt-8">
                                {/* Header */}
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                    <div>
                                        <h3 className="text-base font-bold text-gray-900">Active Checklist Items</h3>
                                        <p className="text-gray-400 text-sm font-medium">
                                            Items for "{selectedTemplate.name}"
                                        </p>
                                    </div>
                                    <Button
                                        className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 px-4 whitespace-nowrap w-fit"
                                        onClick={() => {
                                            setNewItemQuestion('');
                                            setNewItemMandatory(false);
                                            setAddItemDialogOpen(true);
                                        }}
                                    >
                                        <Plus size={16} />
                                        <span>Add Item</span>
                                    </Button>
                                </div>

                                {/* Items Table */}
                                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                                    <div className="overflow-x-auto">
                                        <table className="w-full border-collapse">
                                            <thead>
                                                <tr className="border-b border-gray-100">
                                                    <th className="px-4 py-3.5 text-left text-xs font-bold text-gray-500 w-8">#</th>
                                                    <th className="px-4 py-3.5 text-left text-xs font-bold text-gray-500 italic">Question</th>
                                                    <th className="px-4 py-3.5 text-center text-xs font-bold text-gray-500 w-28">Mandatory</th>
                                                    <th className="px-4 py-3.5 text-center text-xs font-bold text-gray-500 w-24">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50">
                                                {itemsLoading ? (
                                                    [...Array(5)].map((_, i) => (
                                                        <tr key={i} className="animate-pulse">
                                                            <td colSpan={4} className="px-4 py-5">
                                                                <div className="h-5 bg-gray-50 rounded-lg w-full" />
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : activeItems.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={4} className="px-4 py-12 text-center text-gray-400">
                                                            <div className="flex flex-col items-center">
                                                                <ClipboardCheck size={28} className="mb-2 opacity-30" />
                                                                <p className="text-sm font-medium">No checklist items yet</p>
                                                                <p className="text-xs mt-1">Add your first checklist item to get started</p>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    activeItems.map((item, index) => (
                                                        <tr key={item.id} className="hover:bg-gray-50/50 transition-colors group">
                                                            <td className="px-4 py-3.5">
                                                                <div className="flex items-center gap-2">
                                                                    <GripVertical size={14} className="text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab" />
                                                                    <span className="text-sm font-medium text-gray-400">{index + 1}</span>
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3.5">
                                                                <span className="text-sm font-medium text-gray-800">{item.question}</span>
                                                            </td>
                                                            <td className="px-4 py-3.5">
                                                                <div className="flex justify-center">
                                                                    <Switch
                                                                        checked={item.isMandatory}
                                                                        onCheckedChange={() => handleToggleMandatory(item)}
                                                                    />
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3.5">
                                                                <div className="flex items-center justify-center gap-1.5">
                                                                    <Button
                                                                        variant="outline"
                                                                        size="icon"
                                                                        className="w-8 h-8 rounded-lg border-gray-100 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-100 transition-all shadow-sm"
                                                                        onClick={() => handleEditItem(item)}
                                                                        title="Edit Item"
                                                                    >
                                                                        <Edit size={14} />
                                                                    </Button>
                                                                    <Button
                                                                        variant="outline"
                                                                        size="icon"
                                                                        className="w-8 h-8 rounded-lg border-gray-100 hover:bg-amber-50 hover:text-amber-600 hover:border-amber-100 transition-all shadow-sm"
                                                                        onClick={() => handleArchiveItem(item)}
                                                                        title="Archive Item"
                                                                    >
                                                                        <Archive size={14} />
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Important Note */}
                                <div className="flex items-start gap-3 px-4 py-3 bg-orange-50/50 border border-orange-100 rounded-xl">
                                    <AlertTriangle size={16} className="text-orange-500 shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-semibold text-gray-700">Important:</p>
                                        <p className="text-xs text-gray-500 mt-0.5 italic">
                                            Archiving a row removes it from new inspections but keeps it visible in past inspection records for auditing purposes.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {/* ============================================================ */}
                {/* DIALOGS */}
                {/* ============================================================ */}

                {/* Rename Dialog */}
                <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Rename Template</DialogTitle>
                            <DialogDescription>
                                Enter a new name for this inspection template.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="py-4">
                            <Label htmlFor="template-name">Template Name</Label>
                            <Input
                                id="template-name"
                                value={newTemplateName}
                                onChange={(e) => setNewTemplateName(e.target.value)}
                                placeholder="Enter template name"
                                className="mt-2"
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleRenameSubmit();
                                }}
                            />
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setRenameDialogOpen(false)} className="flex items-center gap-2">
                                <X size={16} />
                                Cancel
                            </Button>
                            <Button onClick={handleRenameSubmit} disabled={saving || !newTemplateName.trim()} className="flex items-center gap-2">
                                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={16} />}
                                Save
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* New Template Dialog */}
                <Dialog open={newTemplateDialogOpen} onOpenChange={setNewTemplateDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Create New Template</DialogTitle>
                            <DialogDescription>
                                Create a new inspection template for {selectedCategory?.label || selectedAssetType}.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="py-4 space-y-2">
                            <Label htmlFor="new-template-name">Template Name <span className="text-red-500">*</span></Label>
                            <Input
                                id="new-template-name"
                                value={createTemplateName}
                                onChange={(e) => setCreateTemplateName(e.target.value)}
                                placeholder="e.g., Detailed Vehicle Inspection"
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleCreateTemplate();
                                }}
                            />
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setNewTemplateDialogOpen(false)} className="flex items-center gap-2">
                                <X size={16} />
                                Cancel
                            </Button>
                            <Button onClick={handleCreateTemplate} disabled={saving || !createTemplateName.trim()} className="flex items-center gap-2">
                                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={16} />}
                                Create Template
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Add Item Dialog */}
                <Dialog open={addItemDialogOpen} onOpenChange={setAddItemDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Add Checklist Item</DialogTitle>
                            <DialogDescription>
                                Add a new item to the "{selectedTemplate?.name}" checklist.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="py-4 space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="item-question">Question <span className="text-red-500">*</span></Label>
                                <Input
                                    id="item-question"
                                    value={newItemQuestion}
                                    onChange={(e) => setNewItemQuestion(e.target.value)}
                                    placeholder="e.g., Tyres have safe tread depth and no damage"
                                    autoFocus
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleAddItem();
                                    }}
                                />
                            </div>
                            <div className="flex items-center justify-between">
                                <Label htmlFor="item-mandatory">Mandatory</Label>
                                <Switch
                                    id="item-mandatory"
                                    checked={newItemMandatory}
                                    onCheckedChange={setNewItemMandatory}
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setAddItemDialogOpen(false)} className="flex items-center gap-2">
                                <X size={16} />
                                Cancel
                            </Button>
                            <Button onClick={handleAddItem} disabled={saving || !newItemQuestion.trim()} className="flex items-center gap-2">
                                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus size={16} />}
                                Add Item
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Edit Item Dialog */}
                <Dialog open={editItemDialogOpen} onOpenChange={setEditItemDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Edit Checklist Item</DialogTitle>
                            <DialogDescription>
                                Update this checklist item.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="py-4 space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="edit-item-question">Question <span className="text-red-500">*</span></Label>
                                <Input
                                    id="edit-item-question"
                                    value={editItemQuestion}
                                    onChange={(e) => setEditItemQuestion(e.target.value)}
                                    placeholder="Enter question"
                                    autoFocus
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleEditItemSubmit();
                                    }}
                                />
                            </div>
                            <div className="flex items-center justify-between">
                                <Label htmlFor="edit-item-mandatory">Mandatory</Label>
                                <Switch
                                    id="edit-item-mandatory"
                                    checked={editItemMandatory}
                                    onCheckedChange={setEditItemMandatory}
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setEditItemDialogOpen(false)} className="flex items-center gap-2">
                                <X size={16} />
                                Cancel
                            </Button>
                            <Button onClick={handleEditItemSubmit} disabled={saving || !editItemQuestion.trim()} className="flex items-center gap-2">
                                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={16} />}
                                Save Changes
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                {/* </DialogContent>
        </Dialog> */}
            </div >

            {/* Electrical Settings Section */}
            {
                activeTab === 'electrical' && (
                    <div className="space-y-4 animate-in fade-in duration-300">
                        <ElectricalSettings />
                    </div>
                )
            }

            {/* Lifting Settings Section */}
            {
                activeTab === 'lifting' && (
                    <div className="space-y-4 animate-in fade-in duration-300">
                        <LiftingSettings />
                    </div>
                )
            }

            {/* Custom Fields Section */}
            {
                activeTab === 'fields' && (
                    <div className="space-y-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div>
                                <h3 className="text-base font-bold text-gray-900">Custom Fields</h3>
                                <p className="text-gray-400 text-sm font-medium">
                                    Manage additional fields for {assetTypeCategories.find(c => c.id === selectedAssetType)?.label}
                                </p>
                            </div>
                            <Button
                                className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 px-4 whitespace-nowrap w-fit"
                                onClick={() => {
                                    setNewFieldName('');
                                    setNewFieldType('text');
                                    setNewFieldOptions('');
                                    setNewFieldRequired(false);
                                    setAddFieldDialogOpen(true);
                                }}
                            >
                                <Plus size={16} />
                                <span>Add Custom Field</span>
                            </Button>
                        </div>

                        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                            {fieldsLoading ? (
                                <div className="p-12 flex justify-center">
                                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                                </div>
                            ) : customFields.length === 0 ? (
                                <div className="p-12 text-center flex flex-col items-center">
                                    <div className="h-16 w-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                                        <Plus className="h-8 w-8 text-gray-400" />
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-900">No Custom Fields</h3>
                                    <p className="text-gray-500 mt-1 max-w-sm mb-6">
                                        Create your first custom field to collect specific information for this asset type.
                                    </p>
                                    <Button onClick={() => setAddFieldDialogOpen(true)}>Add Custom Field</Button>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full border-collapse">
                                        <thead>
                                            <tr className="border-b border-gray-100">
                                                <th className="px-4 py-3.5 text-left text-xs font-bold text-gray-500 w-16">Order</th>
                                                <th className="px-4 py-3.5 text-left text-xs font-bold text-gray-500">Field Name</th>
                                                <th className="px-4 py-3.5 text-left text-xs font-bold text-gray-500 w-32">Type</th>
                                                <th className="px-4 py-3.5 text-center text-xs font-bold text-gray-500 w-28">Required</th>
                                                <th className="px-4 py-3.5 text-center text-xs font-bold text-gray-500 w-28">Status</th>
                                                <th className="px-4 py-3.5 text-right text-xs font-bold text-gray-500 w-24">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {customFields.map((field, index) => (
                                                <tr key={field.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                                                    <td className="px-4 py-3 bg-gray-50/30 text-center font-mono text-sm font-bold text-gray-600">
                                                        {field.listOrder}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="text-sm font-bold text-gray-900">{field.fieldName}</div>
                                                        {field.options && field.options.length > 0 && (
                                                            <div className="text-xs text-gray-500 mt-1">
                                                                Options: {field.options.join(', ')}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <Badge variant="outline" className="bg-gray-50 flex items-center justify-center">
                                                            {field.fieldType}
                                                        </Badge>
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        {field.isRequired ? (
                                                            <Badge variant="outline" className="bg-red-50 text-red-600 border-red-100 flex items-center justify-center">Yes</Badge>
                                                        ) : (
                                                            <Badge variant="outline" className="bg-gray-50 text-gray-500 border-gray-200 flex items-center justify-center">No</Badge>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 flex justify-center">
                                                        <Switch
                                                            checked={field.isActive}
                                                            onCheckedChange={() => handleToggleFieldActive(field)}
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center justify-end gap-1">
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-8 w-8 text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
                                                                onClick={() => handleMoveUp(index)}
                                                                disabled={index === 0}
                                                                title="Move Up"
                                                            >
                                                                <ArrowUp size={16} />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-8 w-8 text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
                                                                onClick={() => handleMoveDown(index)}
                                                                disabled={index === customFields.length - 1}
                                                                title="Move Down"
                                                            >
                                                                <ArrowDown size={16} />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                                                                onClick={() => promptDeleteField(field)}
                                                                title="Delete"
                                                            >
                                                                <Trash2 size={16} />
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* Add Custom Field Dialog */}
                        <Dialog open={addFieldDialogOpen} onOpenChange={setAddFieldDialogOpen}>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>Add Custom Field</DialogTitle>
                                    <DialogDescription>
                                        Add a new information field for {assetTypeCategories.find(c => c.id === selectedAssetType)?.label} assets.
                                    </DialogDescription>
                                </DialogHeader>
                                <div className="py-4 space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="field-name">Field Name <span className="text-red-500">*</span></Label>
                                        <Input
                                            id="field-name"
                                            value={newFieldName}
                                            onChange={(e) => setNewFieldName(e.target.value)}
                                            placeholder="e.g., License Plate"
                                            autoFocus
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="field-type">Field Type</Label>
                                        <select
                                            id="field-type"
                                            value={newFieldType}
                                            onChange={(e) => setNewFieldType(e.target.value as any)}
                                            className="w-full text-sm border-gray-300 rounded-md shadow-sm outline-none px-3 py-2 border focus:border-blue-500 focus:ring-blue-500"
                                        >
                                            <option value="text">Text (Short input)</option>
                                            <option value="number">Number</option>
                                            <option value="select">Select (Dropdown)</option>
                                            <option value="boolean">Yes/No (Boolean)</option>
                                            <option value="date">Date</option>
                                        </select>
                                    </div>
                                    {newFieldType === 'select' && (
                                        <div className="space-y-2">
                                            <Label htmlFor="field-options">Options (comma separated) <span className="text-red-500">*</span></Label>
                                            <Input
                                                id="field-options"
                                                value={newFieldOptions}
                                                onChange={(e) => setNewFieldOptions(e.target.value)}
                                                placeholder="e.g., Option 1, Option 2, Option 3"
                                            />
                                            <p className="text-xs text-gray-500">Provide a comma-separated list of options for the dropdown.</p>
                                        </div>
                                    )}
                                    <div className="flex items-center justify-between">
                                        <Label htmlFor="field-required">Is Required?</Label>
                                        <Switch
                                            id="field-required"
                                            checked={newFieldRequired}
                                            onCheckedChange={setNewFieldRequired}
                                        />
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button variant="outline" onClick={() => setAddFieldDialogOpen(false)} className="flex items-center gap-2">
                                        <X size={16} />
                                        Cancel
                                    </Button>
                                    <Button onClick={() => setCreateConfirmDialogOpen(true)} disabled={saving || !newFieldName.trim() || (newFieldType === 'select' && !newFieldOptions.trim())} className="flex items-center gap-2">
                                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus size={16} />}
                                        Create Field
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>

                        <AlertDialog open={createConfirmDialogOpen} onOpenChange={setCreateConfirmDialogOpen}>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle className="flex items-center gap-2 text-slate-900">
                                        <AlertTriangle size={20} className="text-amber-500" />
                                        Confirm Custom Field Creation
                                    </AlertDialogTitle>
                                    <AlertDialogDescription>
                                        The custom field name <span className="font-bold text-slate-900">"{newFieldName}"</span> cannot be changed later and is permanent. Please ensure the name is correct before proceeding.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleCreateField();
                                            setCreateConfirmDialogOpen(false);
                                        }}
                                        className="bg-slate-950 hover:bg-slate-900 text-white"
                                        disabled={saving}
                                    >
                                        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus size={16} className="mr-2" />}
                                        Confirm and Create
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>

                        <AlertDialog open={deleteFieldDialogOpen} onOpenChange={setDeleteFieldDialogOpen}>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                                        <AlertTriangle size={20} />
                                        Warning: You Are About to Delete Important Information
                                    </AlertDialogTitle>
                                    <AlertDialogDescription asChild>
                                        <div className="space-y-4 pt-2">
                                            <div className="text-gray-700">
                                                This field will no longer appear on any future assets you create.<br />
                                                All historical information will remain unaffected.
                                            </div>
                                            <div className="space-y-2 pt-2">
                                                <Label htmlFor="delete-confirmation" className="text-gray-900 font-medium">
                                                    To proceed, please type <span className="font-bold">DELETE</span> below.
                                                </Label>
                                                <Input
                                                    id="delete-confirmation"
                                                    value={deleteConfirmationText}
                                                    onChange={(e) => setDeleteConfirmationText(e.target.value)}
                                                    placeholder="DELETE"
                                                    className="border-gray-300 focus:border-red-500 focus:ring-red-500"
                                                />
                                            </div>
                                        </div>
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter className="mt-4">
                                    <AlertDialogCancel disabled={saving} className="border-gray-300 text-gray-700 hover:bg-gray-50">
                                        Cancel
                                    </AlertDialogCancel>
                                    <AlertDialogAction
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleDeleteField();
                                        }}
                                        className="bg-red-600 hover:bg-red-700 focus:ring-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                        disabled={saving || deleteConfirmationText !== 'DELETE'}
                                    >
                                        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : undefined}
                                        Confirm Deletion
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    </div >
                )
            }

            {/* Organization Settings Section */}
            {
                activeTab === 'organization' && (
                    <div className="space-y-4 animate-in fade-in duration-300">
                        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden p-6 max-w-4xl">
                            {orgLoading ? (
                                <div className="flex justify-center p-6">
                                    <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                                </div>
                            ) : (
                                <div className="space-y-8">
                                    <div className="flex gap-3 items-end">
                                        <div className="space-y-2 flex-1 max-w-sm">
                                            <Label htmlFor="companyName" className="font-bold text-gray-700">Add New Organization</Label>
                                            <Input
                                                id="companyName"
                                                value={newOrgName}
                                                onChange={(e) => setNewOrgName(e.target.value)}
                                                placeholder="Enter registered company name"
                                            />
                                        </div>
                                        <Button
                                            onClick={handleAddOrganization}
                                            disabled={saving || !newOrgName.trim()}
                                            className="bg-slate-950 hover:bg-slate-900 text-white"
                                        >
                                            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus size={16} className="mr-2" />}
                                            Add Organization
                                        </Button>
                                    </div>

                                    <div className="space-y-4">
                                        <h4 className="font-bold text-gray-900 border-b pb-2">Registered Organizations</h4>
                                        {organizations.length === 0 ? (
                                            <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed">
                                                <p className="text-gray-500 text-sm">No organizations added yet.</p>
                                            </div>
                                        ) : (
                                            <div className="border rounded-lg overflow-hidden bg-white">
                                                <table className="min-w-full divide-y divide-gray-200">
                                                    <thead className="bg-gray-50">
                                                        <tr>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company Name</th>
                                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                        {organizations.map((org) => (
                                                            <tr key={org.id} className="hover:bg-slate-50 transition-colors group">
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                    {org.companyName}
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                                    <div className="flex items-center justify-end gap-2">
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                                                                            onClick={() => setOrganizationToDelete({ id: org.id, companyName: org.companyName })}
                                                                            title="Delete"
                                                                        >
                                                                            <Trash2 size={16} />
                                                                        </Button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                        <p className="text-xs text-gray-500 mt-2">
                                            These companies will be available to select when generating Asset Labels (QR/Barcodes).
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Delete organization confirmation dialog */}
                        <AlertDialog open={organizationToDelete !== null} onOpenChange={(open) => { if (!open) setOrganizationToDelete(null); }}>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                                        <AlertTriangle size={20} />
                                        Delete Organization
                                    </AlertDialogTitle>
                                    <AlertDialogDescription>
                                        Are you sure you want to delete <span className="font-semibold text-gray-900">"{organizationToDelete?.companyName}"</span>? This action cannot be undone.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter className="mt-4">
                                    <AlertDialogCancel disabled={saving} className="border-gray-300 text-gray-700 hover:bg-gray-50">
                                        Cancel
                                    </AlertDialogCancel>
                                    <AlertDialogAction
                                        onClick={(e) => {
                                            e.preventDefault();
                                            confirmDeleteOrganization();
                                        }}
                                        className="bg-red-600 hover:bg-red-700 focus:ring-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                        disabled={saving}
                                    >
                                        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : undefined}
                                        Confirm Delete
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    </div>
                )
            }
        </div >
        // </div >
    );
};

export default AssetSettings;
